import DynamicTable from '@atlaskit/dynamic-table';
import { SpinnerMask } from '@atlassian/amkt-common';
import { useFeatureFlags } from '@atlassian/mpac-feature-flags';
import { LineChart } from '@atlassian/mpac-ui';
import { DynamicTableUtils } from '@atlassian/mpac-utils';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { REPORTS_ROWS_PER_PAGE } from '../../../constants/Preferences';
import NoResultView from '../../../utils/NoResultView';
import AnnualMonthlyHeader from '../Common/AnnualMonthlyHeader';
import { findNoOfDaysBetweenDataPoints, getXLabelFormatterForDayWidth, LineChartWrapper } from '../Common/ChartUtils';
import ErrorMessage from '../Common/ErrorMessage';
import { REPORTS_NO_RESULT_SUBTITLE, REPORTS_NO_RESULT_TITLE } from '../PartnerReports.constants';
import ReportChartTooltip from '../ReportChartTooltip';
import ChartControls from '../ReportsFilters/ChartControls';
import { useRowClick } from '../useReportsRowClick';
import { getChartData, getChartLinesDetails, getChartTooltipPairs, getDatasetsByAppName } from './CloudChurnOrRenewals.utils';
import { getCloudChurnOrRenewalsSummary } from './CloudChurnOrRenewalsSummary';
import CloudChurnOrRenewalsSummary from './CloudChurnOrRenewalsSummary/CloudChurnOrRenewalsSummary';
import { getCloudChurnOrRenewalsBody, getCloudReportHeader } from './CloudChurnOrRenewalsTableData';
import { useCloudChurnOrRenewal } from './hooks';
const chartTooltip = _ref => {
  let {
    active,
    label,
    payload,
    lines
  } = _ref;
  if (active && payload && payload.length) {
    const pairs = getChartTooltipPairs(payload, lines);
    return /*#__PURE__*/React.createElement(ReportChartTooltip, {
      valueWidth: "130px",
      head: label,
      pairs: pairs
    });
  }
  return null;
};

// eslint-disable-next-line max-lines-per-function -- has hooks + many data computation methods
const CloudChurnOrRenewals = props => {
  var _props$queryParams$pe;
  const {
    rowClick
  } = useRowClick(props.partnerId, props.reportName);
  const {
    cloudChurnOrRenewalAPIResponse,
    pending,
    error
  } = useCloudChurnOrRenewal(props.mpacBaseUrl, props.partnerId, props.reportName, props.queryParams);
  const rowsPerPage = DynamicTableUtils.getRowsPerPage(props.reportingApps.length, REPORTS_ROWS_PER_PAGE);
  let chartData = [];
  if (cloudChurnOrRenewalAPIResponse && cloudChurnOrRenewalAPIResponse.total.datasets.length > 0) {
    chartData = getChartData(cloudChurnOrRenewalAPIResponse, props.reportName, props.queryParams.addon);
  }
  const noOfDaysBetweenDataPoints = findNoOfDaysBetweenDataPoints(chartData);

  // X-axis for charts contains date strings
  const xLabelFormat = value => getXLabelFormatterForDayWidth(noOfDaysBetweenDataPoints)(value.toString());
  const cloudChurnOrRenewalSummary = getCloudChurnOrRenewalsSummary(props.reportName, rowClick, cloudChurnOrRenewalAPIResponse === null || cloudChurnOrRenewalAPIResponse === void 0 ? void 0 : cloudChurnOrRenewalAPIResponse.total.datasets);
  const {
    enableReportsAccessibilityChanges
  } = useFeatureFlags();
  return /*#__PURE__*/React.createElement(React.Fragment, null, error && /*#__PURE__*/React.createElement(ErrorMessage, {
    error: error.error,
    status: error.status
  }), !error && /*#__PURE__*/React.createElement(SpinnerMask, {
    active: pending,
    size: 50
  }, !pending && /*#__PURE__*/React.createElement(React.Fragment, null, cloudChurnOrRenewalAPIResponse && cloudChurnOrRenewalAPIResponse.total.datasets.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CloudChurnOrRenewalsSummary, {
    datasets: getDatasetsByAppName(cloudChurnOrRenewalAPIResponse, props.queryParams.addon),
    reportName: props.reportName,
    period: ((_props$queryParams$pe = props.queryParams.period) === null || _props$queryParams$pe === void 0 ? void 0 : _props$queryParams$pe.toString()) || '',
    canUsePatternLines: props.linePatternsUsageUserPreference.value
  }), /*#__PURE__*/React.createElement(LineChartWrapper, {
    enableReportsAccessibilityChanges: enableReportsAccessibilityChanges
  }, /*#__PURE__*/React.createElement(LineChart, {
    lines: getChartLinesDetails(props.linePatternsUsageUserPreference.value, props.reportName),
    data: chartData,
    xLabelFormat: xLabelFormat,
    yLabelFormat: value => "".concat(value, "%"),
    tooltip: chartTooltip,
    isChartDeEmphasised: props.linePatternsUsageUserPreference.isUpdateLinePatternUsageUserPreferencePending
  }), /*#__PURE__*/React.createElement(ChartControls, {
    linePatternsUsageUserPreference: {
      value: props.linePatternsUsageUserPreference.value,
      onPreferenceChange: props.linePatternsUsageUserPreference.onPreferenceChange
    }
  })), /*#__PURE__*/React.createElement(AnnualMonthlyHeader, null)), /*#__PURE__*/React.createElement(DynamicTable, {
    head: getCloudReportHeader(props.reportName),
    rows: [...cloudChurnOrRenewalSummary, ...getCloudChurnOrRenewalsBody(cloudChurnOrRenewalAPIResponse, props.reportName, props.reportingApps, rowClick, props.queryParams.addon)],
    isLoading: pending,
    rowsPerPage: rowsPerPage,
    defaultSortKey: "monthlyRate",
    defaultSortOrder: "DESC",
    emptyView: /*#__PURE__*/React.createElement(NoResultView, {
      title: REPORTS_NO_RESULT_TITLE,
      subtitle: REPORTS_NO_RESULT_SUBTITLE
    })
  }))));
};
export default /*#__PURE__*/React.memo(CloudChurnOrRenewals, isEqual);